import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTenantCrud } from '../hooks/useTenantData';
import { useClub } from '../contexts/ClubContext';
import { sendOrderConfirmationEmail, isEmailConfigured } from '../services/emailService';

function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { club, clubId } = useClub();
  const { add: addOrder } = useTenantCrud('orders');
  const [cart, setCart] = useState([]);
  const [step, setStep] = useState(1); // 1: Review, 2: Customer Info, 3: Payment
  const [processing, setProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState('');
  
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    notes: ''
  });

  const cities = [
    'Port of Spain', 'San Fernando', 'Chaguanas', 'Arima', 'Point Fortin',
    'Scarborough (Tobago)', 'Couva', 'Sangre Grande', 'Tunapuna', 'Marabella',
    'Diego Martin', 'San Juan', 'Fyzabad', 'Princes Town', 'Other'
  ];

  useEffect(() => {
    // Get cart from navigation state
    if (location.state?.cart) {
      setCart(location.state.cart);
    } else {
      // No cart, redirect back to shop
      navigate('/kitshop');
    }
  }, [location.state, navigate]);

  const cartTotal = cart.reduce((total, item) => total + item.price, 0);
  const deliveryFee = 50; // TT$ 50 flat delivery fee
  const orderTotal = cartTotal + deliveryFee;

  const removeFromCart = (cartId) => {
    setCart(cart.filter(item => item.cartId !== cartId));
    if (cart.length === 1) {
      navigate('/kitshop');
    }
  };

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo({ ...customerInfo, [name]: value });
  };

  const validateCustomerInfo = () => {
    const { firstName, lastName, email, phone, address, city } = customerInfo;
    if (!firstName || !lastName || !email || !phone || !address || !city) {
      alert('Please fill in all required fields');
      return false;
    }
    if (!email.includes('@')) {
      alert('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handlePayment = async () => {
    if (!validateCustomerInfo()) return;
    
    setProcessing(true);
    
    try {
      // Create order using tenant-scoped hook (clubId added automatically)
      const orderData = {
        customer: customerInfo,
        items: cart.map(item => ({
          productId: item.id,
          name: item.name,
          size: item.size,
          price: item.price
        })),
        subtotal: cartTotal,
        deliveryFee: deliveryFee,
        total: orderTotal,
        status: 'pending_payment',
        paymentMethod: 'wipay'
      };

      const newOrder = await addOrder(orderData);
      const newOrderId = newOrder.id;
      
      // In production, this would redirect to WiPay
      // For now, simulate payment success after 2 seconds
      // WiPay integration would look like:
      // window.location.href = `https://wipay.co.tt/api/pay?account=${WIPAY_ACCOUNT}&amount=${orderTotal}&...`
      
      setTimeout(async () => {
        // Update order status to paid (would normally be done via webhook)
        // For now just set the local state
        setOrderId(newOrderId);
        setOrderComplete(true);
        setProcessing(false);
        
        // Send order confirmation email if configured
        if (isEmailConfigured()) {
          const orderForEmail = {
            id: newOrderId,
            items: cart.map(item => ({
              name: item.name,
              size: item.size,
              price: item.price,
              quantity: 1
            })),
            customerEmail: customerInfo.email,
            customerName: `${customerInfo.firstName} ${customerInfo.lastName}`,
            subtotal: cartTotal,
            deliveryFee: deliveryFee,
            total: orderTotal,
            address: customerInfo.address,
            city: customerInfo.city
          };
          sendOrderConfirmationEmail(orderForEmail, club?.name);
        }
      }, 2000);
      
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Error processing order. Please try again.');
      setProcessing(false);
    }
  };

  // Order Complete Screen
  if (orderComplete) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="order-complete">
            <div className="complete-icon">✓</div>
            <h1>Order Confirmed!</h1>
            <p className="order-id">Order #: <strong>{orderId}</strong></p>
            <p>Thank you for your purchase! A confirmation email has been sent to <strong>{customerInfo.email}</strong>.</p>
            
            <div className="order-summary-final">
              <h3>Order Summary</h3>
              {cart.map(item => (
                <div key={item.cartId} className="summary-item">
                  <span>{item.name} ({item.size})</span>
                  <span>TT$ {item.price}</span>
                </div>
              ))}
              <div className="summary-item delivery">
                <span>Delivery</span>
                <span>TT$ {deliveryFee}</span>
              </div>
              <div className="summary-item total">
                <span>Total Paid</span>
                <span>TT$ {orderTotal.toLocaleString()}</span>
              </div>
            </div>

            <div className="delivery-info">
              <h4>Delivery Address</h4>
              <p>{customerInfo.firstName} {customerInfo.lastName}</p>
              <p>{customerInfo.address}</p>
              <p>{customerInfo.city}</p>
              <p>{customerInfo.phone}</p>
            </div>

            <p className="delivery-note">Your order will be delivered within 3-5 business days.</p>
            
            <button className="btn-continue-shopping" onClick={() => navigate('/kitshop')}>
              CONTINUE SHOPPING
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-header">
        <h1>CHECKOUT</h1>
        <div className="checkout-steps">
          <div className={`checkout-step ${step >= 1 ? 'active' : ''}`}>
            <span className="step-number">1</span>
            <span className="step-label">Review</span>
          </div>
          <div className="step-line"></div>
          <div className={`checkout-step ${step >= 2 ? 'active' : ''}`}>
            <span className="step-number">2</span>
            <span className="step-label">Details</span>
          </div>
          <div className="step-line"></div>
          <div className={`checkout-step ${step >= 3 ? 'active' : ''}`}>
            <span className="step-number">3</span>
            <span className="step-label">Payment</span>
          </div>
        </div>
      </div>

      <div className="checkout-container">
        <div className="checkout-main">
          {/* Step 1: Review Cart */}
          {step === 1 && (
            <div className="checkout-section">
              <h2>Review Your Order</h2>
              <div className="cart-items">
                {cart.map(item => (
                  <div key={item.cartId} className="cart-item">
                    <div className="item-image">
                      {item.image ? (
                        <img src={item.image} alt={item.name} />
                      ) : (
                        <div className="item-placeholder">🏷️</div>
                      )}
                    </div>
                    <div className="item-details">
                      <h3>{item.name}</h3>
                      <p className="item-size">Size: {item.size}</p>
                      <p className="item-price">TT$ {item.price}</p>
                    </div>
                    <button 
                      className="remove-item"
                      onClick={() => removeFromCart(item.cartId)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <div className="checkout-buttons">
                <button className="btn-back-shop" onClick={() => navigate('/kitshop')}>
                  ← Back to Shop
                </button>
                <button className="btn-next" onClick={() => setStep(2)}>
                  Continue →
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Customer Information */}
          {step === 2 && (
            <div className="checkout-section">
              <h2>Delivery Information</h2>
              <div className="customer-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={customerInfo.firstName}
                      onChange={handleCustomerChange}
                      placeholder="Enter first name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={customerInfo.lastName}
                      onChange={handleCustomerChange}
                      placeholder="Enter last name"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={customerInfo.email}
                      onChange={handleCustomerChange}
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={customerInfo.phone}
                      onChange={handleCustomerChange}
                      placeholder="868-123-4567"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Street Address *</label>
                  <input
                    type="text"
                    name="address"
                    value={customerInfo.address}
                    onChange={handleCustomerChange}
                    placeholder="Enter delivery address"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>City/Town *</label>
                  <select
                    name="city"
                    value={customerInfo.city}
                    onChange={handleCustomerChange}
                    required
                  >
                    <option value="">Select city/town</option>
                    {cities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Order Notes (Optional)</label>
                  <textarea
                    name="notes"
                    value={customerInfo.notes}
                    onChange={handleCustomerChange}
                    placeholder="Special delivery instructions..."
                    rows={3}
                  />
                </div>
              </div>
              <div className="checkout-buttons">
                <button className="btn-back" onClick={() => setStep(1)}>
                  ← Back
                </button>
                <button className="btn-next" onClick={() => {
                  if (validateCustomerInfo()) setStep(3);
                }}>
                  Continue to Payment →
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Payment */}
          {step === 3 && (
            <div className="checkout-section">
              <h2>Payment</h2>
              <div className="payment-options">
                <div className="payment-option selected">
                  <div className="payment-radio">●</div>
                  <div className="payment-info">
                    <h4>WiPay - Card Payment</h4>
                    <p>Pay securely with your Visa, Mastercard, or local debit card</p>
                    <div className="payment-cards">
                      <span className="card-icon">💳</span>
                      <span>Visa</span>
                      <span>Mastercard</span>
                      <span>LINX</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="payment-notice">
                <p>🔒 Your payment is secure. You will be redirected to WiPay to complete your payment.</p>
              </div>

              <div className="checkout-buttons">
                <button className="btn-back" onClick={() => setStep(2)}>
                  ← Back
                </button>
                <button 
                  className="btn-pay"
                  onClick={handlePayment}
                  disabled={processing}
                >
                  {processing ? 'Processing...' : `PAY TT$ ${orderTotal.toLocaleString()}`}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Order Summary Sidebar */}
        <div className="checkout-sidebar">
          <div className="order-summary">
            <h3>Order Summary</h3>
            <div className="summary-items">
              {cart.map(item => (
                <div key={item.cartId} className="summary-line">
                  <span>{item.name} ({item.size})</span>
                  <span>TT$ {item.price}</span>
                </div>
              ))}
            </div>
            <div className="summary-subtotal">
              <span>Subtotal</span>
              <span>TT$ {cartTotal.toLocaleString()}</span>
            </div>
            <div className="summary-delivery">
              <span>Delivery</span>
              <span>TT$ {deliveryFee}</span>
            </div>
            <div className="summary-total">
              <span>Total</span>
              <span>TT$ {orderTotal.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Checkout;
